import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CommonEquipmentService, CommonGlobalService, Equipment, EquipmentIcon, FileExcelIcon, LocationIcon, MultiSelect, RootState, ShortUser, UserIcon } from '@umanforge/common';
import { UserService } from 'services/users.service';

import BaseModal from './base_modal.component';
import { dayjs } from '@umanforge/common';
import { ChartService } from 'services/chart.service';
import { ChartFilter } from 'interfaces/chart.interface';
import ModalButton from '@common_components/buttons/modalButton.component';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import { TaskIcon } from 'components/icons/task.icon';
import { TaskActionService } from 'services/taskAction.service';
import { LocationService } from 'services/location.service';
import { TaskAction } from 'interfaces/taskAction.interface';
import { Location } from 'interfaces/location.interface';
import { SelectOption } from 'interfaces/select_option.interfaces';


import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

dayjs.extend( utc );
dayjs.extend( timezone );

interface ExtractionModalProps {
  onClose: () => void;
  planningId: string;
  sectorId: string;
  dates: { dateStart: string, dateEnd: string }
  defaultFilters: ChartFilter | undefined
}

interface ExtractionErrors {
  name: string | undefined;
  dates: string | undefined;
  startHours: string | undefined;
  endHours: string | undefined;
}

const ExtractionModal: React.FC<ExtractionModalProps> = ( {
  onClose,
  planningId,
  sectorId,
  dates,
  defaultFilters
} ) => {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ taskActions, setTaskActions ] = useState<TaskAction[]>( [] );
  const [ locations, setLocations ] = useState<Location[]>( [] );
  const [ equipments, setEquipments ] = useState<Equipment[]>( [] );
  const [ users, setUsers ] = useState<ShortUser[]>( [] );

  const [ selectedTaskActions, setSelectedTaskActions ] = useState<SelectOption<TaskAction>[]>( [] );
  const [ selectedLocations, setSelectedLocations ] = useState<SelectOption<Location>[]>( [] );
  const [ selectedEquipments, setSelectedEquipments ] = useState<SelectOption<Equipment>[]>( [] );
  const [ selectedUsers, setSelectedUsers ] = useState<SelectOption<ShortUser>[]>( [] );

  const [ dateStart, setDateStart ] = useState<dayjs.Dayjs | undefined>( dayjs( dates.dateStart ) );
  const [ dateEnd, setDateEnd ] = useState<dayjs.Dayjs | undefined>( dayjs( dates.dateEnd ) );

  const [ filters, setFilters ] = useState<ChartFilter>( defaultFilters ?? {} as ChartFilter );

  useEffect( () => {
    if ( isFirstRender ) {
      init();
    }
  }, [] );

  useEffect( () => {
    setSelectedTaskActions( [] );
    setSelectedLocations( [] );
    setSelectedEquipments( [] );
    setSelectedUsers( [] );
    fecthPlanningData();
  }, [ sectorId ] );

  const init = () => {
    fecthPlanningData().then( () => {
      setIsFirstRender( false );
    } );
  };

  const handleChange = () => {
    const filterLocations = [ ...new Set( selectedLocations.map( o => { return o.object.id; } ) ) ];
    const filterTypeActions = [ ...new Set( selectedTaskActions.map( o => { return o.object.id; } ) ) ];
    const filterEquipments = [ ...new Set( selectedEquipments.map( o => { return o.object.id; } ) ) ];
    const filterUsers = [ ...new Set( selectedUsers.map( o => { return o.object.id; } ) ) ];
    const _filters = {
      filterLocations,
      filterTypeActions,
      filterEquipments,
      filterUsers
    };
    setFilters( _filters );
  };

  useEffect( () => {
    handleChange();
  }, [
    selectedTaskActions,
    selectedLocations,
    selectedEquipments,
    selectedUsers
  ] );

  const fecthPlanningData = async () => {
    const [ _taskActions, _locations, _equipments, _users ] = await Promise.all( [
      TaskActionService.getTaskActionBySector( sectorId, token, dispatch ),
      LocationService.getLocationBySector( sectorId, token, dispatch ),
      CommonEquipmentService.getAllowedEquipmentsBySectorsId( [ sectorId ], token, dispatch ),
      UserService.getUsersOfSector( sectorId, token, dispatch ),
    ] );
    setTaskActions( _taskActions );
    setLocations( _locations );
    setEquipments( _equipments );
    setUsers( _users );
  };



  const [ errors, setErrors ] = useState<ExtractionErrors>( {
    name: undefined,
    dates: undefined,
    startHours: undefined,
    endHours: undefined
  } );

  const handleCloseModal = () => {
    onClose();
  };


  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  const handleExtract = async () => {
    try {
      const dateStartValid = dateStart?.isValid();
      const dateEndValid = dateEnd?.isValid();
      if ( !dateStartValid || !dateEndValid || !dateStart || !dateEnd || dateStart.isAfter( dateEnd ) ) {
        setErrors( {
          ...errors,
          dates: t( 'translation:invalid_date_range' )
        } );
        return;
      }

      await ChartService.getExtractionByDatesAndPlanningId(
        planningId,
        {
          dateStart: dateStart?.format( 'YYYY-MM-DD' ),
          dateEnd: dateEnd?.format( 'YYYY-MM-DD' ),
          timezone: dayjs.tz.guess(),
          filters: filters
        },
        token, dispatch
      );
      onClose();
    }
    catch ( error ) {
      console.error( 'Error extracting data:', error );
    }
  };


  // Render
  return (
    <>
      <BaseModal
        isOpen={true}
        onClose={handleCloseModal}
        noConfirmationModal
        icon={<FileExcelIcon className='w-12 h-12 bg-green-200 p-2 rounded-md' color='rgb(60, 60, 60)'/>}
        title={ t( 'common:extraction' ) }
        footer={
          <>
            <div className="flex font-semibold px-5 py-2 bg-white-base w-full border-t rounded-b-md border-t-gray-200 flex-center">
              <div className="w-full flex gap-4">
                <div className={ 'flex w-full justify-end' }>
                  <ModalButton
                    label={t( 'translation:extract_data' )}
                    icon={<FileExcelIcon className='w-5 stroke-red-500 text-red-500 fill-red-500 ' color='rgb(250, 250, 250)'/>}
                    onClick={ handleExtract }
                    textColor={'text-white'}
                    bgColor={'bg-app-color hover:bg-blue-600'}
                    borderColor={`${!true && 'border-app-color'}`}
                    hoverBorderColor={'hover:border-blue-600'}
                    width={'w-fit mx-1'}
                  />
                </div>
              </div>
            </div>
          </>
        }
      >
        <div className="py-5 px-7 gap-y-4 bg-white font-texte flex-col flex">

          <div className='lg:flex gap-x-10'>
            <div className='w-full'>
              <div className='items-center lg:flex gap-x-2 w-full'>
                <p className={'text-gray-600 mr-4 left w-4/12' }> { t( 'translation:start_of_extract' ) } </p>
                <DatePickerSoloInput
                  label={ undefined }
                  defaultValue={ dateStart }
                  onChange={ setDateStart }
                  textSize={'w-3/4'}
                />
                { errors.startHours && <p className="text-red-500 h-6 -mt-1">{ errors.startHours }</p> }
              </div>
            </div>

            <div className='w-full lg:mt-0 mt-4'>
              <div className='items-center lg:flex gap-x-2 w-full'>
                <p className={'text-gray-600 mr-4 left w-4/12' }> { t( 'translation:end_of_extract' ) } </p>
                <DatePickerSoloInput
                  label={ undefined }
                  defaultValue={ dateEnd }
                  onChange={ setDateEnd }
                  textSize={'w-3/4'}
                />
                { errors.endHours && <p className="text-red-500 h-6 -mt-1">{ errors.endHours }</p> }
              </div>
            </div>
          </div>

          <MultiSelect
            label={ t( 'translation:locations' ) + ' (' + selectedLocations.length + ')' }
            disabled={ false }
            placeholder={ t( 'translation:locations' ) }
            icon={ <LocationIcon className='w-7 h-7' /> }
            clearable={ true }
            selectedValues={ selectedLocations }
            getValues={ CommonGlobalService.toOptions( locations ) }
            onChange={ setSelectedLocations }
          />

          <MultiSelect
            label={ t( 'common:equipments' ) + ' (' + selectedEquipments.length + ')' }
            disabled={ false }
            placeholder={ t( 'common:equipments' ) }
            icon={ <EquipmentIcon className='w-7 h-7' /> }
            clearable={ true }
            selectedValues={ selectedEquipments }
            getValues={ CommonGlobalService.toOptions( equipments ) }
            onChange={ setSelectedEquipments }
          />

          <MultiSelect
            label={ t( 'translation:task_actions' ) + ' (' + selectedTaskActions.length + ')' }
            disabled={ false }
            placeholder={ t( 'translation:task_actions' ) }
            icon={ <TaskIcon className='w-7 h-7' /> }
            clearable={ true }
            selectedValues={ selectedTaskActions }
            getValues={ CommonGlobalService.toOptions( taskActions ) }
            onChange={ setSelectedTaskActions }
          />

          <MultiSelect
            label={ t( 'common:users' ) + ' (' + selectedUsers.length + ')' }
            disabled={ false }
            placeholder={ t( 'common:users' ) }
            icon={ <UserIcon className='w-7 h-7' /> }
            clearable={ true }
            selectedValues={ selectedUsers }
            getValues={ CommonGlobalService.toOptions( users ) }
            onChange={ setSelectedUsers }
          />

        </div>
      </BaseModal>
    </>
  );
};

export default ExtractionModal;
